import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { combineLatest, firstValueFrom, Subject, takeUntil } from 'rxjs'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { FeatureCollection, Point } from 'geojson'
import { XyzTileModel } from '../models/xyz-tile.model'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { MatchedSignProperties } from '../models/matched-sign.model'

@Injectable()
export class DataMatchedSignsService {
  private matchedSigns: FeatureCollection<any, MatchedSignProperties | any>[] = [
    // Points
    {
      type: 'FeatureCollection',
      features: [],
    },
    // Lines
    {
      type: 'FeatureCollection',
      features: [],
    },
  ]
  public isDataInitialized = false

  // Add request cancellation mechanism
  private cancelPreviousRequest$ = new Subject<void>()

  constructor(
    private readonly http: HttpClient,
    private appState: AppStateService,
    @Inject(APP_CONFIG) private readonly config: AppConfigModel,
  ) {}

  async loadMatchedSignsData(
    isHistoryEnabled: boolean,
    bounds: maplibregl.LngLatBounds,
    timestamp?: Date,
  ) {
    // Cancel any previous ongoing request
    this.cancelPreviousRequest$.next()

    this.isDataInitialized = true

    let matchedSigns: FeatureCollection<any, MatchedSignProperties> | undefined = undefined

    if (isHistoryEnabled && timestamp) {
      matchedSigns = await this.loadMatchedSignsForHistory(timestamp, bounds)
      this.matchedSigns[0].features = matchedSigns.features

      // Add all features to lines
      this.matchedSigns[1].features = matchedSigns.features.map((feature) => {
        const line = feature.properties.lineStringFeature as GeoJSON.Feature<GeoJSON.LineString>
        return {
          ...line,
          id: feature.properties.id,
          properties: { ...line?.properties, id: feature.properties.id },
        }
      })
    } else if (!isHistoryEnabled) {
      matchedSigns = await this.loadMatchedSigns(bounds)
      this.matchedSigns[0].features = matchedSigns.features

      // Add all features to lines
      this.matchedSigns[1].features = matchedSigns.features.map((feature) => {
        const line = feature.properties.lineStringFeature as GeoJSON.Feature<GeoJSON.LineString>
        return {
          ...line,
          id: feature.properties.id,
          properties: { ...line?.properties, id: feature.properties.id },
        }
      })
    }
    return this
  }

  async loadMatchedSigns(
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<any, MatchedSignProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
    }
    const url = new URL(`${this.config.backend_url}vzo/matched-signs`)
    url.searchParams.set('minLng', bounds.getWest().toString())
    url.searchParams.set('minLat', bounds.getSouth().toString())
    url.searchParams.set('maxLng', bounds.getEast().toString())
    url.searchParams.set('maxLat', bounds.getNorth().toString())

    // Add takeUntil to cancel the request when a new one starts
    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, MatchedSignProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )
    return result
  }

  async loadMatchedSignsForHistory(
    timestamp: Date,
    bounds: maplibregl.LngLatBounds,
  ): Promise<FeatureCollection<any, MatchedSignProperties>> {
    const httpOptions = {
      headers: new HttpHeaders({
        accept: 'json',
      }),
      params: {
        timestamp: timestamp.toISOString(),
        minLat: bounds.getSouth().toString(),
        maxLat: bounds.getNorth().toString(),
        minLng: bounds.getWest().toString(),
        maxLng: bounds.getEast().toString(),
      },
    }

    const url = new URL(`${this.config.backend_url}vzo/matched-signs/historic`)

    const result = await firstValueFrom(
      this.http
        .get<FeatureCollection<Point, MatchedSignProperties>>(url.toString(), httpOptions)
        .pipe(takeUntil(this.cancelPreviousRequest$)),
    )

    return result
  }

  getMatchedSigns(): FeatureCollection<any, MatchedSignProperties>[] {
    return this.matchedSigns
  }

  // Method to manually cancel current requests (if needed by external services)
  public cancelCurrentRequests(): void {
    this.cancelPreviousRequest$.next()
  }

  // Clean up on service destruction
  ngOnDestroy(): void {
    this.cancelPreviousRequest$.next()
    this.cancelPreviousRequest$.complete()
  }
}
